import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { TypedMemo } from '../../../common/types';
import { AppPasswordField } from './AppPasswordField';
import { GuideHead } from './GuideHead';
import { GuideHelp } from './GuideHelp';
import { GuideStepCard } from './GuideStepCard';
import { ManualServers } from './ManualServers';
import { MAILBOX_GUIDE_COPY } from './mailboxGuideCopy';
import { GuideServers, providerOf } from './mailboxGuideState';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProviderGuideProps {
  providerId: MailboxProviderId;
  password: string;
  onPassword: (value: string) => void;
  /** The servers that we know (provider table or MX lookup); null: the person types them. */
  knownServers: GuideServers | null;
  manualServers: GuideServers;
  onManualServers: (servers: GuideServers) => void;
  onUseAnother: () => void;
  onAdmin: () => void;
  /** The error box of the last check, shown above the help. */
  error?: React.ReactNode;
}

/**
 * Step 2 of 3 (design outreach-v2 o2, o3, o4): the 3-step guide of the provider, rendered from
 * `MAILBOX_PROVIDERS`, with the password field in its last step. An unsupported provider (Proton,
 * Outlook, Microsoft 365) shows "Use another mailbox" and no field.
 */
const ProviderGuideComponent: React.FC<ProviderGuideProps> = ({
  providerId,
  password,
  onPassword,
  knownServers,
  manualServers,
  onManualServers,
  onUseAnother,
  onAdmin,
  error,
}) => {
  const intl = useIntl();
  const provider = providerOf(providerId);
  const copy = MAILBOX_GUIDE_COPY[provider.id];
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const markOpened = useCallback((key: string) => setOpened((current) => new Set([...current, key])), []);
  const ownLink = useCallback(
    (key: string) => {
      if (key === 'other') {
        onUseAnother();
      }
    },
    [onUseAnother]
  );

  const field = () => {
    if (provider.passwordKind === 'appPassword') {
      return <AppPasswordField providerId={providerId} value={password} onChange={onPassword} />;
    }
    if (provider.passwordKind !== 'emailPassword') {
      return null;
    }
    return (
      <div className="d-flex flex-column gap-2 mt-2">
        <input
          type="password"
          className="form-control"
          value={password}
          autoComplete="new-password"
          aria-label={intl.formatMessage(messages.emailPasswordLabel)}
          onChange={(event) => onPassword(event.target.value)}
        />
        {knownServers === null ? (
          <ManualServers servers={manualServers} onChange={onManualServers} />
        ) : (
          <div className={styles.detected}>
            <FontAwesomeIcon icon={faCheck} />
            <span>
              {intl.formatMessage(messages.serversFound, {
                provider: intl.formatMessage(copy.label),
                imap: knownServers.imapHost,
                smtp: knownServers.smtpHost,
              })}
            </span>
          </div>
        )}
      </div>
    );
  };

  const fieldNode = field();
  const cards = useMemo(() => {
    const now = provider.steps.findIndex((step) => !opened.has(step.key) && step.linkUrl !== undefined);
    const nowIndex = now === -1 ? provider.steps.length - 1 : now;
    return provider.steps.map((step, index) => (
      <GuideStepCard
        key={step.key}
        number={index + 1}
        step={step}
        copy={copy.steps[step.key]}
        state={opened.has(step.key) ? 'done' : index === nowIndex ? 'now' : 'todo'}
        onOpened={markOpened}
        onOwnLink={ownLink}
      >
        {index === provider.steps.length - 1 ? fieldNode : null}
      </GuideStepCard>
    ));
  }, [copy, fieldNode, markOpened, opened, ownLink, provider]);

  return (
    <>
      <GuideHead step={2} title={intl.formatMessage(copy.guideTitle)} intro={intl.formatMessage(copy.guideIntro)} />
      <div className={styles.body}>
        <div className={styles.steps}>{cards}</div>
        {error}
        <GuideHelp providerId={providerId} onAdmin={onAdmin} />
      </div>
    </>
  );
};

export const ProviderGuide = TypedMemo(ProviderGuideComponent);
